import { useState } from "react";
import { Pencil, Loader2, ImageIcon, RefreshCw, Upload, Globe, Lock, Copy, Trash2, Settings, X } from "lucide-react";
import { usePlaylists } from "@/hooks/usePlaylists";
import { usePlaylistCover } from "@/hooks/usePlaylistCover";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import PlaylistCoverCropModal from "@/components/PlaylistCoverCropModal";
import {
  RetroDialog,
  RetroDialogHeader,
  RetroDialogBody,
  RetroDialogFooter,
} from "@/components/ui/retro-dialog";

interface PlaylistEditModalProps {
  open: boolean;
  onClose: () => void;
  playlistId: string;
  currentTitle: string;
  currentDescription: string | null;
  currentCoverUrl?: string | null;
  coverGenerationCount?: number;
  isPublic?: boolean;
  slug?: string;
  onCoverUpdated?: (url: string) => void;
  onVisibilityChanged?: (isPublic: boolean) => void;
  onDelete?: () => void;
}

export default function PlaylistEditModal({
  open,
  onClose,
  playlistId,
  currentTitle,
  currentDescription,
  currentCoverUrl,
  coverGenerationCount = 0,
  isPublic = false,
  slug,
  onCoverUpdated,
  onVisibilityChanged,
  onDelete,
}: PlaylistEditModalProps) {
  const { updatePlaylist } = usePlaylists();
  const { generateCover, generating } = usePlaylistCover();
  const { user } = useAuth();
  const [title, setTitle] = useState(currentTitle);
  const [description, setDescription] = useState(currentDescription || "");
  const [saving, setSaving] = useState(false);
  const [coverUrl, setCoverUrl] = useState(currentCoverUrl);
  const [genCount, setGenCount] = useState(coverGenerationCount);
  const [showCropModal, setShowCropModal] = useState(false);
  const [pub, setPub] = useState(isPublic);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    await updatePlaylist(playlistId, {
      title: title.trim(),
      description: description.trim() || undefined,
      is_public: pub,
    });
    setSaving(false);
    onVisibilityChanged?.(pub);
    toast.success("Playlist updated");
    onClose();
  };

  const handleGenerate = async () => {
    const url = await generateCover(playlistId);
    if (url) {
      setCoverUrl(url);
      setGenCount((c) => c + 1);
      onCoverUpdated?.(url);
    }
  };

  const handleCoverUploaded = (url: string) => {
    setCoverUrl(url);
    onCoverUpdated?.(url);
  };

  const handleRemoveCover = async () => {
    await updatePlaylist(playlistId, { cover_image_url: null } as any);
    setCoverUrl(null);
    onCoverUpdated?.("");
    toast.success("Cover image removed");
  };

  const handleCopyLink = () => {
    if (!slug) return;
    const url = `${window.location.origin}/list/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied!");
  };

  const handleDelete = () => setConfirmingDelete(true);
  const executeDelete = () => {
    onDelete?.();
    onClose();
  };

  const remainingGens = Math.max(0, 4 - genCount);
  const isGenerating = generating === playlistId;

  return (
    <>
      <RetroDialog open={open} onOpenChange={(o) => !o && onClose()} size="md">
        <RetroDialogHeader
          title={
            <span className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" />
              Playlist Settings
            </span>
          }
        />
        <RetroDialogBody className="max-h-[65vh] overflow-y-auto">
          {/* Cover Image Section */}
          <div className="mb-4">
            <label className="font-mono text-[11px] text-muted-foreground uppercase tracking-wider mb-2 block">
              Cover Image
            </label>
            <div className="flex items-start gap-3">
              {coverUrl ? (
                <img
                  src={coverUrl}
                  alt="Playlist cover"
                  className="w-24 h-24 rounded object-cover flex-shrink-0 border-2 border-foreground"
                />
              ) : (
                <div className="w-24 h-24 rounded bg-muted flex items-center justify-center flex-shrink-0 border-2 border-foreground">
                  <ImageIcon className="w-8 h-8 text-muted-foreground/40" />
                </div>
              )}
              <div className="flex flex-col gap-1.5 min-w-0">
                <button
                  onClick={handleGenerate}
                  disabled={isGenerating || remainingGens === 0}
                  className="btn-comic-secondary btn-comic-sm disabled:opacity-50"
                >
                  {isGenerating ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3 h-3" />
                  )}
                  {coverUrl ? "Regenerate" : "Generate"} Cover
                </button>
                <button
                  onClick={() => setShowCropModal(true)}
                  className="btn-comic-secondary btn-comic-sm"
                >
                  <Upload className="w-3 h-3" />
                  Upload Image
                </button>
                {coverUrl && (
                  <button
                    onClick={handleRemoveCover}
                    className="btn-comic-ghost btn-comic-sm text-[hsl(8,72%,46%)] hover:text-[hsl(8,78%,40%)]"
                  >
                    <X className="w-3 h-3" />
                    Remove Cover
                  </button>
                )}
                <p className="font-mono text-[10px] text-muted-foreground">
                  {remainingGens} AI generation{remainingGens !== 1 ? "s" : ""} remaining
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="font-mono text-[11px] text-muted-foreground uppercase tracking-wider mb-1 block">
                Title
              </label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="input-retro"
              />
            </div>

            <div>
              <label className="font-mono text-[11px] text-muted-foreground uppercase tracking-wider mb-1 block">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="What's this playlist about?"
                className="textarea-retro"
              />
              <p className="font-mono text-[10px] text-muted-foreground mt-1">
                {description.length}/160 chars — aim for 120-160 for best SEO
              </p>
            </div>

            {/* Visibility toggle */}
            <div>
              <label className="font-mono text-[11px] text-muted-foreground uppercase tracking-wider mb-2 block">
                Visibility
              </label>
              <div className="flex gap-2">
                <button
                  onClick={() => setPub(true)}
                  className={pub ? "btn-comic-primary btn-comic-sm flex-1" : "btn-comic-secondary btn-comic-sm flex-1"}
                >
                  <Globe className="w-3.5 h-3.5" />
                  Public
                </button>
                <button
                  onClick={() => setPub(false)}
                  className={!pub ? "btn-comic-primary btn-comic-sm flex-1" : "btn-comic-secondary btn-comic-sm flex-1"}
                >
                  <Lock className="w-3.5 h-3.5" />
                  Private
                </button>
              </div>
              {pub && slug && (
                <button
                  onClick={handleCopyLink}
                  className="btn-comic-ghost btn-comic-sm w-full mt-2"
                >
                  <Copy className="w-3 h-3" />
                  Copy Share Link
                </button>
              )}
            </div>
          </div>

          {/* Delete section */}
          <div className="mt-5 pt-4 border-t-2 border-foreground/20">
            {confirmingDelete ? (
              <div className="space-y-2">
                <p className="font-mono text-[11px] text-[hsl(8,72%,46%)]">
                  This will permanently delete this playlist. This can't be undone.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={executeDelete}
                    className="btn-comic-danger btn-comic-sm flex-1"
                  >
                    Yes, Delete
                  </button>
                  <button
                    onClick={() => setConfirmingDelete(false)}
                    className="btn-comic-ghost btn-comic-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={handleDelete}
                className="btn-comic-ghost btn-comic-sm w-full text-[hsl(8,72%,46%)] hover:text-[hsl(8,78%,40%)]"
              >
                <Trash2 className="w-3 h-3" />
                Delete Playlist
              </button>
            )}
          </div>
        </RetroDialogBody>
        <RetroDialogFooter>
          <button onClick={onClose} className="btn-comic-ghost">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!title.trim() || saving}
            className="btn-comic-primary disabled:opacity-50"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Save Changes
          </button>
        </RetroDialogFooter>
      </RetroDialog>

      <PlaylistCoverCropModal
        open={showCropModal}
        onClose={() => setShowCropModal(false)}
        playlistId={playlistId}
        userId={user?.id || ""}
        onCoverUpdated={handleCoverUploaded}
      />
    </>
  );
}
